import React from "react";

// Customizable Area Start
import {
  Box,
  Table,
  Paper,
  styled,
  Button,
  Popover,
  Snackbar,
  TableRow,
  TableBody,
  TableHead,
  IconButton,
  TableContainer,
  TablePagination,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { MoreVert as MoreVertIcon } from "@material-ui/icons";

import SortingTableCell from "../../../components/src/SortingTableCell";
import SortingTableHeader from "../../../components/src/SortingTableHeader2";
import { colors } from "../../../blocks/utilities/src/Colors";
import { TableRowsSkeleton } from "../../../components/src/customComponents/TableRowsSkeleton.web";
// Customizable Area End

import ReportSchedulerController, {
  Props,
  IScheduleItem,
} from "./ReportSchedulerController";

const Strings = {
  pageTitle: "Report Scheduler",
  addSchedule: "Add Schedule",
  noRecordFounds: "No schedules found",
  view: "View",
  edit: "Edit",
  run: "Run",
};

export default class ReportScheduler extends ReportSchedulerController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  renderPopover = () => {
    const { popOverOpened, popOverTop, popOverLeft, permissionStatus } = this.state;

    return (
      <Popover
        open={popOverOpened}
        onClose={this.handleClosePopover}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: popOverTop + 40,
          left: popOverLeft - 80,
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          style: {
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.03), 0px 8px 32px rgba(0, 0, 0, 0.06)",
            borderRadius: "8px",
          },
        }}
      >
        <div style={webStyle.popoverContainer}>
          {permissionStatus.viewPermission && (
            <Box
              data-test-id="viewButton"
              sx={webStyle.popoverButton}
              onClick={this.handleViewSchedule}
            >
              {Strings.view}
            </Box>
          )}
          {permissionStatus.editPermission && (
            <Box
              data-test-id="editButton"
              sx={webStyle.popoverButton}
              onClick={this.handleEditSchedule}
            >
              {Strings.edit}
            </Box>
          )}
          <Box
            data-test-id="runButton"
            sx={webStyle.popoverButton}
            onClick={this.handleRunSchedule}
          >
            {Strings.run}
          </Box>
        </div>
      </Popover>
    );
  };
  // Customizable Area End

  render() {
    // Customizable Area Start
    const {
      totalCount,
      sortingData,
      schedulesList,
      permissionStatus,
      isLoading,
      snackbarOpen,
      snackbarMessage,
      snackbarSeverity,
    } = this.state;
    // Customizable Area End

    return (
      // Customizable Area Start
      <MainBoxWrapper>
        <Box className="pageWrapper" style={{ paddingTop: "10px" }}>
          <div style={webStyle.headerWrapper}>
            <PageTitle>{Strings.pageTitle}</PageTitle>
            <div style={webStyle.headerButtonPartAccount}>
              {permissionStatus.createPermission && (
                <Button
                  data-test-id="addSchedule"
                  className="addButton"
                  onClick={this.handleAddSchedule}
                >
                  {Strings.addSchedule}
                </Button>
              )}
            </div>
          </div>
          <TableContainer component={Paper} className="flexItem">
            <Table style={webStyle.table} aria-label="customized table">
              <TableHead className="stickyHeader">
                <TableRow style={{ background: "#204B9C", color: "#FFFFFF" }}>
                  <SortingTableHeader
                    sortingData={sortingData}
                    title="Name"
                    sortingKey="name"
                    {...this.sortingProps}
                    type="left"
                    minHeightType={true}
                  />
                  <SortingTableHeader
                    sortingData={sortingData}
                    title="Report Type"
                    sortingKey="report_type_label"
                    {...this.sortingProps}
                    minHeightType={true}
                  />
                  <SortingTableHeader
                    sortingData={sortingData}
                    title="Start Date"
                    sortingKey="start_date"
                    {...this.sortingProps}
                    minHeightType={true}
                  />
                  <SortingTableHeader
                    sortingData={sortingData}
                    title="End Date"
                    sortingKey="end_date"
                    {...this.sortingProps}
                    minHeightType={true}
                  />
                  <SortingTableHeader
                    sortingData={sortingData}
                    title=""
                    sortingKey=""
                    {...this.sortingProps}
                    type="action"
                    minHeightType={true}
                    sortingDisabled
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {schedulesList.map((item: IScheduleItem) => (
                  <TableRow
                    key={"table-row-" + item.id}
                    data-test-id={"table-row-" + item.id}
                  >
                    <SortingTableCell
                      width="22vw"
                      type="left"
                      content={item.name}
                    />
                    <SortingTableCell
                      width="22vw"
                      type="middle"
                      content={item.report_type_label || "-"}
                    />
                    <SortingTableCell
                      width="18vw"
                      type="middle"
                      content={item.start_date || "-"}
                    />
                    <SortingTableCell
                      width="18vw"
                      type="middle"
                      content={item.end_date || "-"}
                    />
                    <SortingTableCell
                      width="10vw"
                      type="action"
                      content={
                        <IconButton
                          data-test-id={"moreButton-" + item.id}
                          onClick={(event) => this.handleOpenPopover(event, item.id)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      }
                    />
                  </TableRow>
                ))}
              </TableBody>

              {isLoading && (
                <>
                  {Array(5)
                    .fill(1)
                    .map((_, index) => (
                      <TableBody key={index} data-test-id="skeletonTable">
                        <TableRow>
                          {[...Array(5)].map((_, cellIndex) => (
                            <TableRowsSkeleton key={cellIndex} />
                          ))}
                        </TableRow>
                      </TableBody>
                    ))}
                </>
              )}
            </Table>

            {schedulesList.length === 0 && !isLoading && (
              <NoRecordFoundText>{Strings.noRecordFounds}</NoRecordFoundText>
            )}
          </TableContainer>

          {schedulesList.length !== 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={this.currentPage - 1}
              rowsPerPage={this.rowPerPage}
              rowsPerPageOptions={[10]}
              onRowsPerPageChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                this.rowPerPage = e.target.value as number;
                this.getSchedulesApi();
              }}
              onPageChange={(_, page: number) => this.onChangePage(page + 1)}
            />
          )}
        </Box>
        {this.renderPopover()}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={this.handleCloseSnackbar}
            severity={snackbarSeverity}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </MainBoxWrapper>
      // Customizable Area End
    );
  }
}

// Customizable Area Start
const NoRecordFoundText = styled("div")({
  fontSize: 16,
  fontWeight: 600,
  textAlign: "center",
  padding: "20px",
});

export const PageTitle = styled("div")({
  fontFamily: "Montserrat",
  fontStyle: "normal",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "29px",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "16px",
  },
});

const MainBoxWrapper = styled("div")({
  "& .addButton": {
    background: colors().cyancobaltblue,
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    color: colors().white,
    height: "56px",
    width: "184px",
    borderRadius: "8px",
    textTransform: "unset",
    "&:hover": {
      background: colors().cyancobaltblue,
    },
    "@media only screen and (max-width: 1024px)": {
      fontSize: "13px",
      width: "160px",
      height: "44px",
    },
  },
});

export const webStyle = {
  table: {
    minWidth: 700,
  },
  headerWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 32,
    alignItems: "center",
    flexWrap: "wrap" as "wrap",
  },
  headerButtonPartAccount: {
    display: "flex",
  },
  popoverContainer: {
    paddingTop: "16px",
    paddingBottom: "16px",
    fontFamily: "Montserrat",
    fontWeight: 500,
    fontSize: "14px",
  },
  popoverButton: {
    cursor: "pointer",
    width: "118px",
    height: "33px",
    paddingLeft: "24px",
    paddingTop: "8px",
    backgroundColor: "#FFFFFF",
    color: "#4D4D4D",
    "&:hover": {
      backgroundColor: "#204B9C",
      color: "#FFFFFF",
    },
  },
};
// Customizable Area End
